import { isQuickStudyAttentionTarget, isStudyQuestionnaireAttentionTarget, type AppNotification, type StudyArtifactAttentionReceipt, type StudyArtifactAttentionTarget, type StudyQuestionnaireAttentionTarget } from '@digital-twins/shared-types'

/** Every query value comes from one persisted receipt, never surrounding prose. */
export function studyArtifactAttentionHref(
  receipt: Pick<StudyArtifactAttentionReceipt, 'attentionToken' | 'target'>,
): string {
  const target = receipt.target
  const query = new URLSearchParams(isQuickStudyAttentionTarget(target)
    ? {quickStudy: target.draftToken, quickRevision: String(target.draftRevision), ...(target.stage === 'results' ? {quickRun: target.runToken} : {}), originMessage: target.originMessageToken, attention: receipt.attentionToken}
    : isStudyQuestionnaireAttentionTarget(target)
    ? { studyDraft: target.draftToken, draftRevision: String(target.draftRevision), originMessage: target.originMessageToken, attention: receipt.attentionToken }
    : { study: target.studyToken, originMessage: target.originMessageToken, attention: receipt.attentionToken })
  return `/threads/${encodeURIComponent(target.threadToken)}?${query}`
}

function artifactAttentionMetadata(value: unknown): Pick<StudyArtifactAttentionReceipt, 'attentionToken' | 'target'> | null {
  if (!value || typeof value !== 'object') return null
  const record = value as Record<string, unknown>
  const target = record.target as Record<string, unknown> | undefined
  if (!target || typeof target !== 'object'
    || !asNonEmptyString(record.attentionToken)
    || !asNonEmptyString(target.threadToken)
    || !asNonEmptyString(target.originMessageToken)) return null
  if (target.kind === 'quick_study') {
    if (!asNonEmptyString(target.draftToken) || !Number.isInteger(target.draftRevision) || Number(target.draftRevision) < 1) return null
    const common = {kind: 'quick_study' as const, threadToken: String(target.threadToken), originMessageToken: String(target.originMessageToken), draftToken: String(target.draftToken), draftRevision: Number(target.draftRevision)}
    if (target.stage === 'questions') return {attentionToken: String(record.attentionToken), target: {...common, stage: 'questions'}}
    if (target.stage === 'results' && asNonEmptyString(target.runToken)) return {attentionToken: String(record.attentionToken), target: {...common, stage: 'results', runToken: String(target.runToken)}}
    return null
  }
  if (target.stage === 'questions') {
    if (!asNonEmptyString(target.draftToken) || !Number.isInteger(target.draftRevision) || Number(target.draftRevision) < 0) return null
    return { attentionToken: record.attentionToken as string, target: target as unknown as StudyQuestionnaireAttentionTarget }
  }
  if (target.stage !== undefined || !asNonEmptyString(target.studyToken)
    || !asNonEmptyString(target.artifactToken)
    || !Number.isInteger(target.artifactRevision) || Number(target.artifactRevision) < 1) return null
  return { attentionToken: record.attentionToken as string, target: target as unknown as StudyArtifactAttentionTarget }
}

function asNonEmptyString(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null
  }

  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

function normalizeLegacyChatHref(href: string): string {
  const match = href.match(/^\/chat\/([^/?#]+)/)
  if (match) {
    return `/chats/${match[1]}`
  }

  return href
}

export function getNotificationHref(notification: AppNotification): string | undefined {
  const { type, href, metadata } = notification
  const attention = artifactAttentionMetadata(metadata?.artifactAttention)
  if (attention) return studyArtifactAttentionHref(attention)
  const studyId = asNonEmptyString(metadata?.studyId)
  const audienceId = asNonEmptyString(metadata?.audienceId)
  const chatId = asNonEmptyString(metadata?.chatId)
  const threadToken = asNonEmptyString(metadata?.threadToken)

  if ((type === 'study_completed' || type === 'study_failed') && studyId) {
    return `/tests/${studyId}/results`
  }

  if ((type === 'audience_ready' || type === 'audience_failed') && audienceId) {
    return `/audiences/${audienceId}`
  }

  if (type === 'audience_shared') {
    const token = asNonEmptyString(metadata?.inviteToken)
    if (token) {
      return `/audiences/invites/${token}/accept`
    }
    if (audienceId) {
      return `/audiences/${audienceId}`
    }
  }

  if (type === 'audience_invite_accepted' && audienceId) {
    return `/audiences/${audienceId}`
  }

  if ((type === 'chat_shared' || type === 'chat_viewed') && chatId) {
    return `/chats/${chatId}`
  }

  if (
    (type === 'workflow_run_completed' || type === 'workflow_run_failed') &&
    threadToken
  ) {
    return `/threads/${threadToken}`
  }

  if (!href) {
    return undefined
  }

  return normalizeLegacyChatHref(href)
}
